import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBranchSummaryData } from '../hooks/useBrandQueries';
import { useBranch } from '../contexts/BranchContext';
import { useTimeFrame } from '../contexts/TimeFrameContext';
import LicenseGate from '../components/ui/LicenseGate';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import ErrorMessage from '../components/ui/ErrorMessage';
import EmptyState from '../components/ui/EmptyState';
import { SortControls, BranchSummaryCard, Pagination } from '../components/brand';
import { logger } from '../utils/logger';

interface BranchSummaryDashboardProps {
  brandId?: number;
  timeRange?: string;
}

type SortOption = 'name' | 'revenue' | 'customers' | 'performance';

const BranchSummaryDashboard: React.FC<BranchSummaryDashboardProps> = ({
  brandId: propBrandId,
  timeRange: propTimeRange
}) => {
  // Get context values
  const { selectedBranch } = useBranch();
  const { timeFrame } = useTimeFrame();
  const navigate = useNavigate();
  
  // Use prop values or fall back to context
  const brandId = propBrandId || selectedBranch?.brandId || null;
  const timeRange = propTimeRange || timeFrame || 'last6Months';
  
  // State management for sorting and pagination
  // Pagination acts as lazy loading - only 12 cards rendered at a time
  // This improves performance for brands with 20+ branches
  const [sortBy, setSortBy] = useState<SortOption>('name');
  const [currentPage, setCurrentPage] = useState(1);
  const [filterRange, setFilterRange] = useState<'all' | 'high' | 'medium' | 'low'>('all');
  const ITEMS_PER_PAGE = 12; // Optimal number for performance and UX
  
  // Define thresholds for metrics (can be customized per brand)
  const THRESHOLDS = {
    revenue: {
      high: 0.8, // 80% of brand average or higher
      low: 0.5   // Below 50% of brand average
    },
    satisfaction: {
      high: 80,  // 80% or higher
      low: 60    // Below 60%
    },
    clubMembership: {
      high: 30,  // 30% or higher
      low: 15    // Below 15%
    }
  };
  
  // Fetch branch summary data
  const { data, isLoading, error, refetch } = useBranchSummaryData(brandId, timeRange);
  
  // Handle error logging
  React.useEffect(() => {
    if (error) {
      logger.error('Failed to load branch summary data', 'BranchSummaryDashboard', error);
    }
  }, [error]);
  
  // Calculate performance range for each branch
  const getBranchRange = (branch: any, brandAverage: any): 'high' | 'medium' | 'low' => {
    const revenueRatio = branch.currentMonthRevenue / brandAverage.avgRevenue;
    const satisfaction = branch.satisfactionScore;
    const membership = branch.clubMembershipRate;
    
    // Count how many metrics are in high/low range
    let highCount = 0;
    let lowCount = 0;
    
    if (revenueRatio >= THRESHOLDS.revenue.high) highCount++;
    if (revenueRatio < THRESHOLDS.revenue.low) lowCount++;
    
    if (satisfaction >= THRESHOLDS.satisfaction.high) highCount++;
    if (satisfaction < THRESHOLDS.satisfaction.low) lowCount++;
    
    if (membership >= THRESHOLDS.clubMembership.high) highCount++;
    if (membership < THRESHOLDS.clubMembership.low) lowCount++;
    
    // Determine overall range
    if (highCount >= 2) return 'high';
    if (lowCount >= 2) return 'low';
    return 'medium';
  };
  
  // Memoized sorted and filtered branches
  const sortedBranches = useMemo(() => {
    if (!data?.branches) return [];
    
    // First, filter by range if needed
    let filtered = [...data.branches];
    if (filterRange !== 'all') {
      filtered = filtered.filter(branch => 
        getBranchRange(branch, data.brandAverage) === filterRange
      );
    }
    
    // Then sort
    return filtered.sort((a, b) => {
      switch (sortBy) {
        case 'revenue':
          return b.currentMonthRevenue - a.currentMonthRevenue;
        case 'customers':
          return b.customerCount - a.customerCount;
        case 'performance':
          return b.performanceScore - a.performanceScore;
        case 'name':
        default:
          // Persian alphabetical sort
          return a.branchName.localeCompare(b.branchName, 'fa');
      }
    });
  }, [data?.branches, data?.brandAverage, sortBy, filterRange]);
  
  // Memoized paginated branches - lazy loading implementation
  // Only slices the array when page or sorted data changes
  // This prevents unnecessary re-renders of branch cards
  const paginatedBranches = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    const end = start + ITEMS_PER_PAGE;
    return sortedBranches.slice(start, end);
  }, [sortedBranches, currentPage]);
  
  // Memoized total pages calculation
  const totalPages = useMemo(() => {
    return Math.ceil(sortedBranches.length / ITEMS_PER_PAGE);
  }, [sortedBranches.length]);
  
  // Reset to page 1 when sort or filter changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [sortBy, filterRange]);
  
  // Count branches in each range
  const rangeCounts = useMemo(() => {
    if (!data?.branches) return { high: 0, medium: 0, low: 0 };
    
    return data.branches.reduce((acc, branch) => {
      const range = getBranchRange(branch, data.brandAverage);
      acc[range]++;
      return acc;
    }, { high: 0, medium: 0, low: 0 });
  }, [data?.branches, data?.brandAverage]);
  
  // Show error state
  if (error) {
    return (
      <LicenseGate licenseType="club" dashboardName="خلاصه شعب">
        <div className="container-dashboard" dir="rtl">
          <h1 className="title-dashboard">خلاصه شعب</h1>
          <ErrorMessage
            message="خطا در بارگذاری داده‌های شعب"
            onRetry={() => refetch()}
          />
        </div>
      </LicenseGate>
    );
  }
  
  // Show loading state
  if (isLoading) {
    return (
      <LicenseGate licenseType="club" dashboardName="خلاصه شعب">
        <div className="container-dashboard" dir="rtl">
          <h1 className="title-dashboard">خلاصه شعب</h1>
          <div className="flex flex-col items-center justify-center min-h-[400px]">
            <LoadingSpinner
              variant="minimal"
              size="lg"
              message="در حال بارگذاری داده‌های شعب…"
            />
          </div>
        </div>
      </LicenseGate>
    );
  }
  
  // Show empty state if no data
  if (!data || !data.branches || data.branches.length === 0) {
    return (
      <LicenseGate licenseType="club" dashboardName="خلاصه شعب">
        <div className="container-dashboard" dir="rtl">
          <h1 className="title-dashboard">خلاصه شعب</h1>
          <EmptyState
            message="هیچ شعبه‌ای برای نمایش یافت نشد"
            icon="🏪"
          />
        </div>
      </LicenseGate>
    );
  }
  
  // Handle navigation to branch detail dashboard
  const handleBranchClick = (branchId: number) => {
    navigate(`/dashboard/main?branchId=${branchId}`);
  };
  
  return (
    <LicenseGate licenseType="club" dashboardName="خلاصه شعب">
      <div className="container-dashboard" dir="rtl">
        {/* Skip to main content link for keyboard users */}
        <a
          href="#branch-summary-content"
          className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:right-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-primary-600 focus:text-white focus:rounded-lg focus:shadow-lg"
        >
          پرش به محتوای اصلی
        </a>
        
        <main id="branch-summary-content" className="space-y-6 md:space-y-8 lg:space-y-10 animate-enter-up" role="main" aria-label="داشبورد خلاصه شعب">
          {/* Dashboard title using standard class - responsive sizing */}
          <header>
            <h1 className="title-dashboard text-2xl md:text-3xl" id="dashboard-title">خلاصه شعب</h1>
          </header>
          
          {/* Filter and Sort Controls */}
          <section aria-labelledby="controls-heading">
            <h2 id="controls-heading" className="sr-only">کنترل‌های فیلتر و مرتب‌سازی</h2>
            
            {/* Range Filter Buttons */}
            <div className="card-base card-padding mb-4">
              <h3 className="text-base font-semibold text-text-primary mb-3">
                فیلتر بر اساس عملکرد
              </h3>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setFilterRange('all')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-base ${
                    filterRange === 'all'
                      ? 'bg-primary-600 text-white'
                      : 'bg-bg-secondary text-text-primary hover:bg-bg-tertiary'
                  }`}
                >
                  همه ({data.branches.length.toLocaleString('fa-IR')})
                </button>
                <button
                  onClick={() => setFilterRange('high')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-base flex items-center gap-2 ${
                    filterRange === 'high'
                      ? 'bg-green-600 text-white'
                      : 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/30'
                  }`}
                >
                  <span>🟢</span>
                  عملکرد عالی ({rangeCounts.high.toLocaleString('fa-IR')})
                </button>
                <button
                  onClick={() => setFilterRange('medium')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-base flex items-center gap-2 ${
                    filterRange === 'medium'
                      ? 'bg-yellow-600 text-white'
                      : 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400 hover:bg-yellow-100 dark:hover:bg-yellow-900/30'
                  }`}
                >
                  <span>🟡</span>
                  عملکرد متوسط ({rangeCounts.medium.toLocaleString('fa-IR')})
                </button>
                <button
                  onClick={() => setFilterRange('low')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-base flex items-center gap-2 ${
                    filterRange === 'low'
                      ? 'bg-red-600 text-white'
                      : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30'
                  }`}
                >
                  <span>🔴</span>
                  نیاز به توجه ({rangeCounts.low.toLocaleString('fa-IR')})
                </button>
              </div>
              
              {/* Threshold Info */}
              <div className="mt-4 p-3 bg-bg-tertiary rounded-lg">
                <p className="text-xs text-text-secondary">
                  <strong>معیارهای ارزیابی:</strong> عملکرد بر اساس فروش، رضایت مشتری و نرخ عضویت باشگاه محاسبه می‌شود.
                </p>
              </div>
            </div>
            
            <SortControls value={sortBy} onChange={setSortBy} />
          </section>
          
          {/* Branch Summary Cards Grid - Responsive layout optimized for all screen sizes */}
          <section aria-labelledby="branches-heading" aria-live="polite">
            <h2 id="branches-heading" className="sr-only">
              لیست شعب (صفحه {currentPage.toLocaleString('fa-IR')} از {totalPages.toLocaleString('fa-IR')})
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6" role="list">
              {paginatedBranches.map((branch) => {
                const range = getBranchRange(branch, data.brandAverage);
                return (
                  <div key={branch.branchId} role="listitem">
                    <BranchSummaryCard
                      branch={branch}
                      brandAverage={data.brandAverage}
                      onClick={() => handleBranchClick(branch.branchId)}
                      performanceRange={range}
                    />
                  </div>
                );
              })}
            </div>
          </section>
          
          {/* Pagination Controls */}
          {totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          )}
        </main>
      </div>
    </LicenseGate>
  );
};

export default BranchSummaryDashboard;
